import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./index.css";

// Redux Imports
import { MisReportCreateDownload } from "../../redux/features/Settings/Statistics/index.js";

// Components Imports
import ICONS from "../../assets/Icons/index.js";
import SmallPopup from "../../components/_utils/CommonSmallPopup/index.jsx";
import PrimaryStepper from "../../components/_utils/CommonSteppers/PrimaryStepper/index.jsx";
import CommonToast from "../../components/_utils/CommonToast/index.jsx";
import SVGComponent from "../../components/_utils/SVGComponent/index.jsx";
import MISReportCreateConfirm from "../../components/GenerateMISReportComp/MISReportCreateConfirm/index.jsx";
import MISReportCreateFilter from "../../components/GenerateMISReportComp/MISReportCreateFilter/index.jsx";
import MISReportCreateSelection from "../../components/GenerateMISReportComp/MISReportCreateSelection/index.jsx";
import MISReportCreateSorting from "../../components/GenerateMISReportComp/MISReportCreateSorting/index.jsx";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../utils/configurationProvider.js";
import TEXT from "../../utils/globalText.js";
import SmallPopupWithInput from "../../components/_utils/CommonSmallPopupWithInput/index.jsx";

const GenerateMISReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const services = useSelector((state) => state.refreshToken.services);

  const isAdmin = services?.includes("Admin");
  const FILE_FORMAT_OPTIONS = ["XLSX", "CSV"];
  const SORT_OPTIONS = ["Ascending", "Descending"];

  const [activeStep, setActiveStep] = useState(0);
  const [labelsOptions, setLabelsOptions] = useState([]);
  const [showConfirmLeavePopup, setShowConfirmLeavePopup] = useState(false);
  const [fileFormatDownloadPopup, setFileFormatDownloadPopup] = useState(false);
  const [slideAnimation, setSlideAnimation] = useState(false);
  const [direction, setDirection] = useState(null);
  const [removeClassName, setRemoveClassName] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isDateConfirmed, setIsDateConfirmed] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [endDateConfirm, setendDateConfirm] = useState(null);
  const [startDateConfirm, setstartDateConfirm] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(() => {
    const path = window.location.pathname;

    switch (true) {
      case path.includes("internal_transaction_dashboard"):
        return "Internal transaction";

      case path.includes("beneficiary_transaction_dashboard"):
        return "Beneficiary transaction";

      case path.includes("owner_account_dashboard"):
        return "Bank statement";

      case path.includes("private_transaction_dashboard"):
        return "Private transaction";

      default:
        return "";
    }
  });
  const [dropdownStates, setDropdownStates] = useState({
    dropdown1: false,
    dropdown2: false,
  });
  const [selectedValues, setSelectedValues] = useState({
    dropdown1: "",
    dropdown2: "",
  });
  const [filterMapping, setFilterMapping] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [fileFormat, setFileFormat] = useState("XLSX");
  const [columnsData, setColumnsData] = useState([]);
  const [buttonStatus, setButtonStatus] = useState({
    selectAllColumn: false,
    columnConfirm: true,
    sortConfirm: true,
  });
  const [isFirstConfirmed, setIsFirstConfirmed] = useState(false);
  const [isSortConfirmed, setIsSortConfirmed] = useState(false);
  const [nextButtonEnabled, BenEnableNextButton] = useState(false);
  const steps = [
    TEXT.SELECT_FIELD_DETAILS,
    TEXT.SELECT_COLUMNS,
    TEXT.FILTER_COLUMNS_OPTIONAL,
    TEXT.CONFIRM_AND_DOWNLOAD,
  ];

  const animationClass =
    direction === "next" ? "animate-slide-next" : "animate-slide-prev";

  // Helper functions
  const handleDateformat = (dates) => {
    if (!dates) return "";
    const date = new Date(dates);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleCreateStatus = (name) => {
    const statuses = [
      "distribution_complete",
      "created",
      "approved",
      "awaiting_funds",
      "distribution_initiated",
      "distribution_pending",
      "rejected",
      "failed",
      "reversal",
      "denied",
      "abandoned",
    ];
    return statuses.map((itm) => ({
      field: "distribution_lifecycle_status",
      include_value: name === "Include" ? itm : "",
      exclude_value: name === "Exclude" ? itm : "",
    }));
  };

  const getApprovalStatus = (name) => {
    const statuses = [
      "approved",
      "pending",
      "rejected",
      "abandoned",
      "blocked",
    ];
    return statuses.map((itm) => ({
      field: "lifecycle_status",
      include_value: name === "Include" ? itm : "",
      exclude_value: name === "Exclude" ? itm : "",
    }));
  };

  const getAccountApprovalStatus = (name) => {
    const statuses = ["approved", "pending", "rejected", "blocked", "archived"];
    return statuses.map((itm) => ({
      field: "beneficiary_account_approval_status",
      include_value: name === "Include" ? itm : "",
      exclude_value: name === "Exclude" ? itm : "",
    }));
  };

  const getDistributionStatus = (status, criteria_name) => {
    switch (status) {
      case "distribution complete":
        return "distribution_complete";
      case "distribution created":
        return "created";
      case "distribution initiated":
        return "distribution_initiated";
      case "distribution approved":
        return "approved";
      case "distribution pending":
        return "distribution_pending";
      case "awaiting funds":
        return "awaiting_funds";
      case "distribution reversal":
        return "reversal";
      case "distribution denied":
        return "denied";
      case "distribution rejected":
        return "rejected";
      case "distribution failed":
        return "failed";
      case "distribution abandoned":
        return "abandoned";
      case "all":
        return handleCreateStatus(criteria_name);
      default:
        return status;
    }
  };

  const COLUMN_FIELD_KEY_MAP = {
    "Transaction ID": "txn_id",
    "Transaction initiator": "txn_initiator",
    "Entity ID": "entity_id",
    Category: "category",
    "Beneficiary account PAN": "beneficiary_account_pan_number",
    "Beneficiary account email ID": "beneficiary_account_email",
    "Beneficiary account name": "beneficiary_account_name",
    "Beneficiary account bank name": "beneficiary_account_bank_name",
    "Beneficiary account number": "beneficiary_account_bank_acc_no",
    "Beneficiary account IFSC": "beneficiary_account_ifsc_code",
    "Account status": "beneficiary_account_approval_status",
    Amount: "amount",
    "Distribution remark": "distribution_remark",
    "Owner account bank name": "owner_account_bank_name",
    "Owner account number": "owner_account_bank_acc_no",
    "Owner account IFSC": "owner_account_ifsc_code",
    "Owner account balance": "owner_account_balance",
    "Owner account type": "owner_account_type",
    Labels: "beneficiary_transaction_labels",
    "Created on": "created_on",
    "Payment method": "payment_method",
    "Approved by": "approved_by",
    "Approved on": "approved_at",
    "Rejected by": "rejected_by",
    "Rejected on": "rejected_at",
    "Rejected reason": "rejected_reason",
    "Confidential transaction": "confidential_transaction",
    "Transaction approval status": "lifecycle_status",
    "Transaction distribution status": "distribution_lifecycle_status",
    "Distribution completion date": "distribution_completion_date",
    "Additional recipient": "beneficiary_account_recipient_email",
  };

  const USER_COLUMN_MAP = {
    Username: "user_full_name",
    "Beneficiary account created count":
      "total_beneficiary_account_created_count",
    "Beneficiary transaction created count":
      "total_beneficiary_transaction_created_count",
    "Beneficiary transaction approved count":
      "total_beneficiary_transaction_approved_count",
    "Internal transaction created count":
      "total_internal_transaction_created_count",
    "Internal transaction approved count":
      "internal_transaction_approved_count",
  };

  const BANK_STATEMENT_COLUMN_MAP = {
    "Account ID": "account_id",
    "Opening balance": "opening_balance",
    "Closing balance": "closing_balance",
    "Account statement entry date": "account_statement_entry_date",
    "Account number": "bank_account_number",
    "Account name": "account_name",
    "Credit/Debit": "credit_debit",
    Narration: "narration",
  };

  const getHandleColoums = (status, selectedOption) => {
    if (!status) return "";
    if (selectedOption === "User") {
      return (
        USER_COLUMN_MAP[status] || status.toLowerCase().replace(/\s+/g, "_")
      );
    }
    if (selectedOption === "Bank statement") {
      return (
        BANK_STATEMENT_COLUMN_MAP[status] ||
        status.toLowerCase().replace(/\s+/g, "_")
      );
    }
    return COLUMN_FIELD_KEY_MAP[status] || status;
  };

  // Throttle function
  const throttle = (func, delay) => {
    let inThrottle = false;
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), delay);
      }
    };
  };

  const showToast = (message) => {
    CommonToast({
      text: message,
      icon: ICONS?.LOADER_GIF,
      variant: "var(--processing)",
      buttonNeeded: false,
      btnProps: {
        enabledBgColor: "var(--primary-bg)",
        enabledTextColor: "var(--text-primary)",
        btnText: "",
        handleFunction: () => {},
        icon: "",
        btnType: "button",
        isDisabled: false,
        hoverOption: "white",
      },
    });
  };

  // Main API call function
  const handleCreateBiller = async () => {
    setLoading(true);
    const selectedColumns = columnsData
      ?.filter((col) => col.selected)
      ?.map((itm) => getHandleColoums(itm.columnName, selectedOption));

    const sortdata = {
      field: getHandleColoums(selectedValues?.dropdown1, selectedOption) || "",
      order: selectedValues?.dropdown2 === "Ascending" ? "asc" : "desc",
    };

    let val = [];
    let filterData =
      filterMapping?.map((itm) => {
        if (
          itm.input_value === "all" &&
          itm.column_name === "Transaction distribution status"
        ) {
          const d = getDistributionStatus(itm.input_value, itm.criteria_name);
          val = [...val, ...d];
          return null;
        } else if (
          itm.input_value === "all" &&
          itm.column_name === "Transaction approval status"
        ) {
          const d = getApprovalStatus(itm.criteria_name);
          val = [...val, ...d];
          return null;
        } else if (
          itm.input_value === "all" &&
          itm.column_name === "Account status"
        ) {
          const d = getAccountApprovalStatus(itm.criteria_name);
          val = [...val, ...d];
          return null;
        } else {
          return {
            field: getHandleColoums(itm.column_name, selectedOption),
            include_value:
              itm.criteria_name === "Include"
                ? itm.column_name === "Transaction distribution status"
                  ? getDistributionStatus(itm.input_value, itm.criteria_name)
                  : itm.column_name === "Transaction approval status" ||
                      itm.column_name === "Account status"
                    ? itm.input_value
                    : itm.column_name === "Confidential transaction"
                      ? true
                      : itm.input_value
                : "",
            exclude_value:
              itm.criteria_name === "Exclude"
                ? itm.column_name === "Transaction distribution status"
                  ? getDistributionStatus(itm.input_value, itm.criteria_name)
                  : itm.column_name === "Transaction approval status" ||
                      itm.column_name === "Account status"
                    ? itm.input_value
                    : itm.column_name === "Confidential transaction"
                      ? true
                      : itm.input_value
                : "",
            similar_value:
              itm.criteria_name === "Similar" ? itm.input_value : "",
            max_value: itm.criteria_name === "Maximum" ? itm.input_value : "",
            min_value: itm.criteria_name === "Minimum" ? itm.input_value : "",
          };
        }
      }) || [];

    filterData = [...filterData, ...val]?.filter((itm) => itm !== undefined);

    const selectedFormat = (fileFormat || "XLSX").toString().toLowerCase();

    const payload = {
      report_details: {
        report_type:
          selectedOption === "Beneficiary transaction"
            ? "beneficiary_transaction"
            : selectedOption === "User"
              ? "user"
              : selectedOption === "Bank statement"
                ? "statement"
                : "internal_transaction",
        selected_columns: selectedColumns,
        date_from:
          startDateConfirm && endDateConfirm
            ? handleDateformat(startDateConfirm)
            : undefined,
        date_to:
          startDateConfirm && endDateConfirm
            ? handleDateformat(endDateConfirm)
            : undefined,
      },
      filters:
        filterData.length > GlobalConfiguration?.lengthCheckZero
          ? filterData
          : undefined,
      sort_by:
        selectedValues?.dropdown1 !== "" && selectedValues?.dropdown2 !== ""
          ? [sortdata]
          : undefined,
    };

    sessionStorage.setItem("MisPayload", JSON.stringify(payload));
    sessionStorage.setItem("Misfromdate", JSON.stringify(startDateConfirm));
    sessionStorage.setItem("Mistodate", JSON.stringify(endDateConfirm));
    showToast("Generating report");
    const res = await dispatch(
      MisReportCreateDownload({
        payload,
        formatType: selectedFormat,
      }),
    );

    if (res.meta.requestStatus === "fulfilled" && res.payload === true) {
      toast.dismiss();
      CommonToast({
        text: "Report generated and download started.",
        icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        variant: "var(--success)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });
      setLoading(false);
      navigate(-1);
    } else {
      setLoading(false);
    }
  };

  const throttledHandleCreateTransaction = throttle(handleCreateBiller, 5000);

  const hasAdditionalColumnsSelected = useCallback(() => {
    if (selectedOption === "User") {
      const selectedColumns = columnsData.filter(
        (data) => data?.selected === true,
      );
      return (
        selectedColumns.length > GlobalConfiguration?.lengthCheckOne ||
        (selectedColumns.length === GlobalConfiguration?.lengthCheckOne &&
          selectedColumns[0].columnName !== "Username")
      );
    } else if (selectedOption === "Bank statement") {
      const selectedColumns = columnsData.filter(
        (data) => data?.selected === true,
      );
      return (
        selectedColumns.length > GlobalConfiguration?.lengthCheckOne ||
        (selectedColumns.length === GlobalConfiguration?.lengthCheckOne &&
          selectedColumns[0].columnName !== "Account ID")
      );
    } else {
      return columnsData.some((data) => data?.selected === true);
    }
  }, [selectedOption, columnsData]);

  const toggleDropdown = (dropdownKey) => {
    setDropdownStates((prevState) => ({
      ...prevState,
      [dropdownKey]: !prevState[dropdownKey],
    }));
  };

  const handleOptionSelect = (dropdownKey, option) => {
    setSelectedValues((prevValues) => {
      const newValues = {
        ...prevValues,
        [dropdownKey]: option,
      };

      if (dropdownKey === "dropdown1") {
        newValues.dropdown2 = "";
      }

      return newValues;
    });
    toggleDropdown(dropdownKey);
    // Auto-confirm sort for all field types when both dropdowns are selected
    setTimeout(() => {
      const currentValues = { ...selectedValues, [dropdownKey]: option };
      if (dropdownKey === "dropdown1") {
        currentValues.dropdown2 = "";
      }
      const hasValidSort =
        currentValues.dropdown1 !== "" && currentValues.dropdown2 !== "";
      if (hasValidSort) {
        setButtonStatus((previosData) => ({
          ...previosData,
          sortConfirm: false,
        }));
        setIsSortConfirmed(true);
      }
    }, 100);
  };

  const handleClearFilter = (dropdownKey, e) => {
    e.stopPropagation();
    setSelectedValues((prevValues) => ({
      ...prevValues,
      [dropdownKey]: "",
    }));
  };

  const handleSelectColumn = (selectedId) => {
    setColumnsData((previousData) => {
      return previousData?.map((result) => {
        if (
          (selectedOption === "User" && result.columnName === "Username") ||
          (selectedOption === "Bank statement" &&
            result.columnName === "Account ID")
        ) {
          return { ...result, selected: true };
        }

        return result.columnId === selectedId
          ? { ...result, selected: !result.selected }
          : result;
      });
    });

    setSelectedValues({
      dropdown1: "",
      dropdown2: "",
    });

    setButtonStatus((prev) => ({
      ...prev,
      columnConfirm: false,
      sortConfirm: true,
    }));

    setIsSortConfirmed(false);
    setIsFirstConfirmed(true);
  };

  const handleSelectAllColumn = () => {
    if (buttonStatus?.selectAllColumn) {
      setColumnsData((previousData) => {
        return previousData?.map((result) => {
          if (selectedOption === "User" && result.columnName === "Username") {
            return { ...result, selected: true };
          }
          if (
            selectedOption === "Bank statement" &&
            result.columnName === "Account ID"
          ) {
            return { ...result, selected: true };
          }
          return { ...result, selected: false };
        });
      });
      setButtonStatus((previosData) => {
        return {
          ...previosData,
          selectAllColumn: false,
          columnConfirm: false,
          sortConfirm: true,
        };
      });
    } else {
      setColumnsData((previousData) => {
        return previousData?.map((result) => {
          return { ...result, selected: true };
        });
      });
      setButtonStatus((previosData) => {
        return {
          ...previosData,
          selectAllColumn: true,
          columnConfirm: false,
          sortConfirm: true,
        };
      });
    }
    // Auto-confirm for select all - similar to handleSelectColumn
    setTimeout(() => {
      setIsFirstConfirmed(true);
      setIsSortConfirmed(false);
    }, 100);
  };

  const handleClearColumns = () => {
    setColumnsData((previousData) =>
      previousData.map((result) => {
        if (selectedOption === "User" && result.columnName === "Username") {
          return { ...result, selected: true };
        }
        if (
          selectedOption === "Bank statement" &&
          result.columnName === "Account ID"
        ) {
          return { ...result, selected: true };
        }
        return { ...result, selected: false };
      }),
    );
    setButtonStatus((previosData) => ({
      ...previosData,
      columnConfirm: true,
      selectAllColumn: false,
    }));
    setIsFirstConfirmed(false);
  };

  const handeleColumnConfirm = () => {
    setSelectedValues((prevValues) => ({
      ...prevValues,
      dropdown1: "",
      dropdown2: "",
    }));

    setButtonStatus((previosData) => ({
      ...previosData,
      columnConfirm: false,
      sortConfirm: true,
    }));

    setIsSortConfirmed(false);
    setIsFirstConfirmed(true);
    setFilterMapping([]);
  };

  const handleEditSelectedColumns = () => {
    setButtonStatus((previosData) => {
      return { ...previosData, columnConfirm: true };
    });
  };

  const handleClearSort = () => {
    setSelectedValues((prevValues) => ({
      ...prevValues,
      dropdown1: "",
      dropdown2: "",
    }));
    setButtonStatus((previosData) => ({
      ...previosData,
      sortConfirm: true,
    }));
    setIsSortConfirmed(false);
  };

  const handleSortConfirm = () => {
    if (selectedValues.dropdown1 !== "" && selectedValues.dropdown2 !== "") {
      setButtonStatus((previosData) => {
        return { ...previosData, sortConfirm: false };
      });
      setIsSortConfirmed(true);
    }
  };

  const handleEditSort = () => {
    setButtonStatus((previosData) => {
      return { ...previosData, sortConfirm: true };
    });
  };

  const selectedColumns = columnsData.filter((col) => col.selected);

  const handleNext = () => {
    if (activeStep === 3) {
      setFileFormatDownloadPopup(true);
      return;
    }

    setActiveStep(
      (prevActiveStep) => prevActiveStep + GlobalConfiguration?.lengthCheckOne,
    );
    setDirection("next");
    setRemoveClassName(false);
    setTimeout(() => {
      setRemoveClassName(true);
    }, IntervalConfiguration.intervalSemiMid);
  };
  const isFormDirty = () => {
    const path = window.location.pathname;
    let initialOption = "";
    if (path.includes("internal_transaction_dashboard")) {
      initialOption = "Internal transaction";
    } else if (path.includes("beneficiary_transaction_dashboard")) {
      initialOption = "Beneficiary transaction";
    } else if (path.includes("owner_account_dashboard")) {
      initialOption = "Bank statement";
    } else if (path.includes("private_transaction_dashboard")) {
      initialOption = "Private transaction";
    }
    return (
      activeStep > GlobalConfiguration?.lengthCheckZero ||
      selectedOption !== initialOption ||
      startDateConfirm !== null ||
      endDateConfirm !== null
    );
  };

  const isFilterActive = filterMapping.some((itm) => itm.active === true);

  const handleBack = () => {
    if (activeStep === GlobalConfiguration?.lengthCheckTwo && isFilterActive)
      return;
    if (activeStep === GlobalConfiguration?.lengthCheckZero) {
      if (isFormDirty()) {
        setShowConfirmLeavePopup(true);
      } else {
        navigate(-1);
      }
    } else {
      setActiveStep(
        (prevActiveStep) =>
          prevActiveStep - GlobalConfiguration?.lengthCheckOne,
      );
      setDirection("previous");
      setRemoveClassName(false);
      setTimeout(() => {
        setRemoveClassName(true);
      }, IntervalConfiguration.intervalSemiMid);
      setTimeout(() => {
        setDirection(null);
      }, 500);
    }
  };

  const ALL_COLUMNS = [
    { columnId: "col-1", columnName: "Transaction ID", selected: false },
    { columnId: "col-2", columnName: "Transaction initiator", selected: false },
    { columnId: "col-3", columnName: "Entity ID", selected: false },
    { columnId: "col-4", columnName: "Category", selected: false },
    {
      columnId: "col-5",
      columnName: "Beneficiary account PAN",
      selected: false,
    },
    {
      columnId: "col-6",
      columnName: "Beneficiary account name",
      selected: false,
    },
    {
      columnId: "col-7",
      columnName: "Beneficiary account bank name",
      selected: false,
    },
    {
      columnId: "col-8",
      columnName: "Beneficiary account number",
      selected: false,
    },
    {
      columnId: "col-9",
      columnName: "Beneficiary account IFSC",
      selected: false,
    },
    {
      columnId: "col-10",
      columnName: "Beneficiary account email ID",
      selected: false,
    },
    {
      columnId: "col-11",
      columnName: "Account status",
      selected: false,
    },
    { columnId: "col-12", columnName: "Amount", selected: false },
    { columnId: "col-13", columnName: "Distribution remark", selected: false },
    {
      columnId: "col-14",
      columnName: "Owner account bank name",
      selected: false,
    },
    { columnId: "col-15", columnName: "Owner account number", selected: false },
    {
      columnId: "col-16",
      columnName: "Owner account IFSC",
      selected: false,
    },
    {
      columnId: "col-17",
      columnName: "Owner account balance",
      selected: false,
    },
    { columnId: "col-18", columnName: "Owner account type", selected: false },
    { columnId: "col-19", columnName: "Labels", selected: false },
    { columnId: "col-20", columnName: "Created on", selected: false },
    { columnId: "col-21", columnName: "Payment method", selected: false },
    { columnId: "col-22", columnName: "Approved by", selected: false },
    { columnId: "col-23", columnName: "Approved on", selected: false },
    { columnId: "col-24", columnName: "Rejected by", selected: false },
    { columnId: "col-25", columnName: "Rejected on", selected: false },
    { columnId: "col-26", columnName: "Rejected reason", selected: false },
    {
      columnId: "col-27",
      columnName: "Confidential transaction",
      selected: false,
    },
    {
      columnId: "col-28",
      columnName: "Transaction approval status",
      selected: false,
    },
    {
      columnId: "col-29",
      columnName: "Transaction distribution status",
      selected: false,
    },
    {
      columnId: "col-30",
      columnName: "Distribution completion date",
      selected: false,
    },
    { columnId: "col-31", columnName: "Additional recipient", selected: false },
  ];

  const USER_COLUMNS = [
    {
      columnId: "user-col-1",
      columnName: "Username",
      selected: true,
      isDefault: true,
    },
    {
      columnId: "user-col-2",
      columnName: "Beneficiary account created count",
      selected: false,
    },
    {
      columnId: "user-col-3",
      columnName: "Beneficiary transaction created count",
      selected: false,
    },
    {
      columnId: "user-col-4",
      columnName: "Internal transaction created count",
      selected: false,
    },
    {
      columnId: "user-col-5",
      columnName: "Beneficiary transaction approved count",
      selected: false,
    },
    {
      columnId: "user-col-6",
      columnName: "Internal transaction approved count",
      selected: false,
    },
  ];

  const BANK_COLUMNS = [
    {
      columnId: "bank-col-1",
      columnName: "Account ID",
      selected: true,
      isDefault: true,
    },
    {
      columnId: "bank-col-2",
      columnName: "Account statement entry date",
      selected: false,
    },
    { columnId: "bank-col-3", columnName: "Opening balance", selected: false },
    { columnId: "bank-col-4", columnName: "Closing balance", selected: false },
    { columnId: "bank-col-5", columnName: "Account number", selected: false },
    { columnId: "bank-col-6", columnName: "Account name", selected: false },
    { columnId: "bank-col-7", columnName: "Credit/Debit", selected: false },
    { columnId: "bank-col-8", columnName: "Narration", selected: false },
  ];

  const getFilteredColumns = (option, isAdmin) => {
    if (option === "User") return USER_COLUMNS;
    if (option === "Bank statement") return BANK_COLUMNS;

    return ALL_COLUMNS.filter((column) => {
      if (
        option === "Internal transaction" &&
        column.columnName === "Category"
      ) {
        return false;
      }
      if (column.columnName === "Entity ID") {
        return isAdmin;
      }
      return true;
    });
  };

  const dataArray = [
    {
      previousText: TEXT.CANCEL,
      nextText: TEXT.NEXT,
      previousEnableBgColor: "var(--primary-nav-act)",
      nextEnableBgColor: "var(--text-primary)",
      nextEnableTextColor: "var(--primary-bg)",
      nextHoverOption: "black",
      previousDisableBgColor: "var(--primary-nav-act)",
      nextDisableBgColor: "var(--text-disabled)",
      nextIsLoading: false,
      nextIsEnabled: nextButtonEnabled,
      stepperText: TEXT.SELECT_FIELD_DETAILS,
    },
    {
      previousText: TEXT.PREVIOUS,
      nextText: TEXT.NEXT,
      previousEnableBgColor: "var(--primary-nav-act)",
      nextEnableBgColor: "var(--text-primary)",
      nextEnableTextColor: "var(--primary-bg)",
      nextHoverOption: "black",
      previousDisableBgColor: "var(--primary-nav-act)",
      nextDisableBgColor: "var(--text-disabled)",
      nextIsLoading: false,
      nextIsEnabled: nextButtonEnabled,
      stepperText: TEXT.SELECT_COLUMNS,
    },
    {
      previousText: TEXT.PREVIOUS,
      nextText: TEXT.NEXT,
      previousEnableBgColor: "var(--primary-nav-act)",
      nextEnableBgColor: "var(--text-primary)",
      nextHoverOption: "black",
      nextEnableTextColor: "var(--primary-bg)",
      previousDisableBgColor: "var(--primary-nav-act)",
      nextDisableBgColor: "var(--text-disabled)",
      nextIsLoading: isFilterActive,
      nextIsEnabled: nextButtonEnabled,
      stepperText: TEXT.FILTER_COLUMNS_OPTIONAL,
      extraInfoHeaderText: TEXT.FILTER_COLUMN_INFO,
      extraInfoShow: true,
    },
    {
      previousText: TEXT.PREVIOUS,
      nextText: "Confirm",
      previousEnableBgColor: "var(--primary-nav-act)",
      nextHoverOption: "black",
      nextEnableBgColor: "var(--positive-act)",
      previousDisableBgColor: "var(--primary-nav-act)",
      nextDisableBgColor: loading
        ? "var(--positive-act)"
        : "var(--text-disabled)",
      nextIsLoading: loading,
      nextIsEnabled: nextButtonEnabled,
      nextLoaderNeeded: true,
      stepperText: TEXT.CONFIRM_AND_DOWNLOAD,
    },
  ];

  const renderBillerForms = () => {
    switch (activeStep) {
      case 0:
        return (
          <MISReportCreateSelection
            BenEnableNextButton={BenEnableNextButton}
            activeStep={activeStep}
            direction={direction}
            setDirection={setDirection}
            BenEnableNextButton1={BenEnableNextButton}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            setstartDateConfirm={setstartDateConfirm}
            setendDateConfirm={setendDateConfirm}
            endDateConfirm={endDateConfirm}
            startDateConfirm={startDateConfirm}
            setShowDatePicker={setShowDatePicker}
            showDatePicker={showDatePicker}
            isDateConfirmed={isDateConfirmed}
            setIsDateConfirmed={setIsDateConfirmed}
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            setFilterMapping={setFilterMapping}
            setActiveTab={setActiveTab}
          />
        );
      case 1:
        return (
          <MISReportCreateSorting
            slideAnimation={slideAnimation}
            setSlideAnimation={setSlideAnimation}
            BenEnableNextButton={BenEnableNextButton}
            nextButtonEnabled={nextButtonEnabled}
            activeStep={activeStep}
            direction={direction}
            setDirection={setDirection}
            // Lifted state props
            dropdownStates={dropdownStates}
            setDropdownStates={setDropdownStates}
            selectedValues={selectedValues}
            setSelectedValues={setSelectedValues}
            columnsData={columnsData}
            setColumnsData={setColumnsData}
            buttonStatus={buttonStatus}
            setButtonStatus={setButtonStatus}
            isFirstConfirmed={isFirstConfirmed}
            setIsFirstConfirmed={setIsFirstConfirmed}
            isSortConfirmed={isSortConfirmed}
            setIsSortConfirmed={setIsSortConfirmed}
            sortOptions={SORT_OPTIONS}
            selectedColumns={selectedColumns}
            // Lifted function props
            toggleDropdown={toggleDropdown}
            handleOptionSelect={handleOptionSelect}
            handleClearFilter={handleClearFilter}
            handleSelectColumn={handleSelectColumn}
            handleSelectAllColumn={handleSelectAllColumn}
            handleClearColumns={handleClearColumns}
            handeleColumnConfirm={handeleColumnConfirm}
            handleEditSelectedColumns={handleEditSelectedColumns}
            handleClearSort={handleClearSort}
            handleSortConfirm={handleSortConfirm}
            handleEditSort={handleEditSort}
            selectedOption={selectedOption}
          />
        );

      case 2:
        return (
          <MISReportCreateFilter
            direction={direction}
            setDirection={setDirection}
            setFilterMapping={setFilterMapping}
            filterMapping={filterMapping}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            columnsData={columnsData}
            selectedOption={selectedOption}
            labelsOptions={labelsOptions}
            setLabelsOptions={setLabelsOptions}
          />
        );
      case 3:
        return (
          <MISReportCreateConfirm
            selectedOption={selectedOption}
            startDate={startDateConfirm}
            endDate={endDateConfirm}
            columnsData={columnsData}
            selectedValues={selectedValues}
            filterMapping={filterMapping}
            direction={direction}
            setDirection={setDirection}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const handleEnterKeyPress = (e) => {
      if (e.key === "Enter" && activeStep === 3) {
        e.preventDefault();
        setFileFormatDownloadPopup(true);
      }
    };

    document.addEventListener("keydown", handleEnterKeyPress);
    return () => {
      document.removeEventListener("keydown", handleEnterKeyPress);
    };
  }, [activeStep, throttledHandleCreateTransaction]);

  useEffect(() => {
    let nxBtnenable;
    if (activeStep === GlobalConfiguration?.lengthCheckZero) {
      nxBtnenable = selectedOption !== "" && startDateConfirm && endDateConfirm;
    } else if (activeStep === GlobalConfiguration?.lengthCheckOne) {
      const isColumnValid =
        selectedOption === "User" ||
        selectedOption === "Bank statement" ||
        (hasAdditionalColumnsSelected() && !buttonStatus.columnConfirm);

      const isSortValid =
        (!buttonStatus.sortConfirm &&
          Object.values(selectedValues).every((itm) => itm !== "")) ||
        Object.values(selectedValues).every((itm) => itm === "");

      nxBtnenable = isColumnValid && isSortValid;
    } else if (activeStep === GlobalConfiguration?.lengthCheckTwo) {
      nxBtnenable = filterMapping.every((itm) => !itm.active);
    } else if (activeStep === 3) {
      nxBtnenable = true;
    }
    BenEnableNextButton(nxBtnenable);
  }, [
    activeStep,
    selectedOption,
    startDate,
    endDate,
    filterMapping,
    columnsData,
    selectedValues,
    buttonStatus,
    startDateConfirm,
    endDateConfirm,
    hasAdditionalColumnsSelected,
  ]);

  useEffect(() => {
    if (columnsData.length === GlobalConfiguration?.lengthCheckZero) return;
    if (columnsData.every((data) => data?.selected === true)) {
      setButtonStatus((prev) => ({ ...prev, selectAllColumn: true }));
    } else {
      setButtonStatus((prev) => ({ ...prev, selectAllColumn: false }));
    }
  }, [columnsData]);

  useEffect(() => {
    const currentlySelectedNames = columnsData
      .filter((col) => col.selected)
      .map((col) => col.columnName);

    const invalidFilterIndex = filterMapping.findIndex(
      (filter) =>
        filter.column_name &&
        !currentlySelectedNames.includes(filter.column_name),
    );

    if (invalidFilterIndex !== -1) {
      setFilterMapping((prevMapping) =>
        prevMapping.filter((filter, idx) => {
          if (idx !== invalidFilterIndex) {
            return filter;
          }
        }),
      );
      setActiveTab(invalidFilterIndex);
    }
  }, [columnsData]);

  useEffect(() => {
    const hasSelected = columnsData.some((col) => col.selected);

    if (!hasSelected) {
      setSelectedValues({
        dropdown1: "",
        dropdown2: "",
      });

      setButtonStatus((prev) => ({
        ...prev,
        sortConfirm: true,
      }));

      setIsSortConfirmed(false);
    }
  }, [columnsData]);

  useEffect(() => {
    const filteredColumns = getFilteredColumns(selectedOption, isAdmin);
    setColumnsData(filteredColumns);

    setSelectedValues({
      dropdown1: "",
      dropdown2: "",
    });
    setButtonStatus((prev) => ({
      ...prev,
      columnConfirm: true,
      sortConfirm: true,
    }));
    setIsFirstConfirmed(false);
    setIsSortConfirmed(false);
    setFilterMapping([]);
    setActiveTab(0);
  }, [selectedOption]);

  return (
    <div className="Mis_report_Container_txn">
      {showConfirmLeavePopup && (
        <div
          className="fixed z-z-index-max flex items-center justify-center top-top-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <SmallPopup
            headerTitle={TEXT?.LEAVE_PAGE_WARNING_HEADER}
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-lg"
            subtext={TEXT?.EXIT_CURRENT_FLOW_NOTE}
            btnOneText={TEXT?.STAY}
            btnOneHandleFunction={() => setShowConfirmLeavePopup(false)}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText={TEXT?.LEAVE}
            btnTwoHandleFunction={() => {
              setShowConfirmLeavePopup(false);
              navigate(-1);
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
          />
        </div>
      )}
      {fileFormatDownloadPopup && (
        <div
          className="fixed z-z-index-max flex items-center justify-center top-top-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <SmallPopupWithInput
            headerTitle="File Format"
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-md"
            secondHeaderTitle={
              "Select the file format to download the MIS report."
            }
            secondHeaderFontColor="text-secondary"
            btnOneText={TEXT.CANCEL}
            btnOneHandleFunction={() => setFileFormatDownloadPopup(false)}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText={TEXT?.DOWNLOAD}
            btnTwoHandleFunction={() => {
              setFileFormatDownloadPopup(false);
              throttledHandleCreateTransaction();
            }}
            inputType={"dropdown"}
            dropDownOptions={FILE_FORMAT_OPTIONS}
            dropDownValue={fileFormat}
            handleDropdownChange={(val) => setFileFormat(val)}
            dropDownPlaceholder="Select format"
            dropDownShowCloseButton={false}
            btnTwoEnabledBgColor="var(--positive-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={!fileFormat}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
            dropdownType="capitalize-case"
          />
        </div>
      )}
      <div className="Qsmd:min-h-navbar-breadcrumb-height w-full Qlg:bg-transparent bg-primary-nav"></div>
      <div
        className={`Mis_report_stepper_Div ${activeStep === GlobalConfiguration?.lengthCheckTwo ? "Qsmd:min-h-topnav-bottom-height min-h-stepper-moble-height" : "min-h-stepper-height"}`}
      >
        <PrimaryStepper
          activeStep={activeStep}
          handleForwardAction={handleNext}
          handleBackwardAction={handleBack}
          dataArray={dataArray}
        />
      </div>

      <div
        className={`flex items-center Qsmd:hidden relative w-full-width h-mobile-create-header bg-primary-nav px-padding-md py-padding-sm top-top-base z-z-index-header shadow-box-shadow-bottom ${
          activeStep === GlobalConfiguration?.lengthCheckTwo
            ? ""
            : "rounded-b-border-radius-sm"
        }`}
      >
        <div
          className="cursor-pointer"
          onClick={() => {
            if (isFormDirty()) {
              setShowConfirmLeavePopup(true);
            } else {
              navigate(-1);
            }
          }}
        >
          <SVGComponent
            url={ICONS?.ARROW_LEFT_WHITE}
            color={"var(--text-primary)"}
            height={"var(--icon-md)"}
            width={"var(--icon-md)"}
            alt="Arrow"
          />
        </div>
        <div className="flex flex-col gap-gap-xs ml-margin-md">
          <p className="text-text-primary text-base">Generate MIS</p>
          <p className="text-text-secondary text-xs">
            {steps[activeStep] || ""}
          </p>
        </div>
      </div>

      {activeStep === GlobalConfiguration?.lengthCheckTwo && (
        <div className="bg-primary-nav w-full Qsmd:hidden relative block shadow-box-shadow-bottom">
          <div
            style={{ maxWidth: "var(--max-content-width)" }}
            className="w-full mx-auto border-t border-primary-bg px-padding-button-l Qmd:px-padding-base"
          >
            <div className="py-padding-button-l flex flex-col gap-gap-xs">
              <p className="text-font-xs h-fit text-text-secondary">
                {TEXT.FILTER_COLUMN_INFO}
              </p>
            </div>
          </div>
        </div>
      )}

      <div
        className={`mis_carousel-container w-full ${activeStep === GlobalConfiguration?.lengthCheckTwo ? "pt-padding-md" : "pt-padding-lg"}`}
      >
        <div
          style={{
            margin: "var(--margin-base) auto",
            maxWidth: "var(--max-content-width)",
          }}
          className={`${
            activeStep === 0 && direction === "next"
              ? ""
              : removeClassName === true
                ? ""
                : animationClass
          }  w-full  mb-margin-md  Qsmd:mb-margin-sm pb-padding-xl relative`}
        >
          {renderBillerForms()}
        </div>
      </div>
      <div className="block Qsmd:hidden h-pagination-height"></div>
    </div>
  );
};

export default GenerateMISReport;
